import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Loader2, Building2, Globe, Mail, Target, ArrowRight, CheckCircle2 } from 'lucide-react';
import { API_BASE_URL as API } from '../config/api';



const INDUSTRIES = [
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'fashion', label: 'Fashion & Apparel' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'tech', label: 'Technology' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'health', label: 'Health & Wellness' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'local_business', label: 'Local Business' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'other', label: 'Other' },
];

const AD_GOALS = [
  { value: 'brand_awareness', label: 'Brand Awareness' },
  { value: 'event_promo', label: 'Event Promotion' },
  { value: 'product_promo', label: 'Product Promotion' },
  { value: 'website_traffic', label: 'Website Traffic' },
  { value: 'lead_generation', label: 'Lead Generation' },
];

export default function AdvertiserSignupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [form, setForm] = useState({
    business_name: '',
    website: '',
    contact_email: '',
    password: '',
    industry: '',
    ad_goals: [],
    bio: ''
  });

  const handleGoalToggle = (goal) => {
    setForm(prev => ({
      ...prev,
      ad_goals: prev.ad_goals.includes(goal)
        ? prev.ad_goals.filter(g => g !== goal)
        : [...prev.ad_goals, goal]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.business_name || !form.contact_email || !form.password || !form.industry) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/advertiser/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSuccess(true);
        toast.success('Advertiser account created!');
      } else {
        toast.error(data.detail || 'Registration failed');
      }
    } catch (err) {
      toast.error('Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Welcome to DMV Soundstage Ads!</h2>
          <p className="text-muted-foreground mb-6">
            Your advertiser account has been created. Log in to start creating campaigns.
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            Log In to Your Account
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h1 className="text-3xl font-bold mb-2">Advertise on DMV Soundstage</h1>
          <p className="text-muted-foreground">
            Reach the DMV&apos;s music community with targeted advertising
          </p>
        </div>

        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Business Info */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Business Information
              </h3>
              
              <div>
                <Label htmlFor="business_name">Business Name *</Label>
                <Input
                  id="business_name"
                  value={form.business_name}
                  onChange={(e) => setForm(prev => ({ ...prev, business_name: e.target.value }))}
                  placeholder="Your Company Name"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="website">Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="website"
                    value={form.website}
                    onChange={(e) => setForm(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://yourwebsite.com"
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="contact_email">Contact Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="contact_email"
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="ads@yourcompany.com"
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Min. 6 characters"
                  required
                />
              </div>
            </div>

            {/* Industry */}
            <div className="space-y-4">
              <div>
                <Label>Industry *</Label>
                <Select value={form.industry} onValueChange={(value) => setForm(prev => ({ ...prev, industry: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(ind => (
                      <SelectItem key={ind.value} value={ind.value}>
                        {ind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ad Goals */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Target className="w-4 h-4" />
                Advertising Goals
              </h3>
              <p className="text-sm text-muted-foreground">What do you want to achieve? (Select all that apply)</p>
              
              <div className="grid grid-cols-2 gap-3">
                {AD_GOALS.map(goal => (
                  <label
                    key={goal.value}
                    className="flex items-center gap-2 p-3 rounded-lg border border-border cursor-pointer hover:bg-muted/30"
                  >
                    <Checkbox
                      checked={form.ad_goals.includes(goal.value)}
                      onCheckedChange={() => handleGoalToggle(goal.value)}
                    />
                    <span className="text-sm">{goal.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Bio */}
            <div>
              <Label htmlFor="bio">About Your Business</Label>
              <Textarea
                id="bio"
                value={form.bio}
                onChange={(e) => setForm(prev => ({ ...prev, bio: e.target.value }))}
                placeholder="Tell us about your business and what you want to promote..."
                rows={3}
              />
            </div>

            {/* Submit */}
            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              {loading ? 'Creating Account...' : 'Create Advertiser Account'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Log in
              </Link>
            </p>
          </form>
        </Card>

        {/* Benefits */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-2">
              <Target className="w-5 h-5 text-primary" />
            </div>
            <h4 className="font-medium mb-1">Targeted Reach</h4>
            <p className="text-xs text-muted-foreground">
              Reach listeners, artists, and creators in DC, MD, and VA
            </p>
          </Card>
          <Card className="p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-2">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <h4 className="font-medium mb-1">Multiple Ad Formats</h4>
            <p className="text-xs text-muted-foreground">
              Sponsored posts, banners, spotlight ads, and event promos
            </p>
          </Card>
          <Card className="p-4 text-center">
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-2">
              <CheckCircle2 className="w-5 h-5 text-primary" />
            </div>
            <h4 className="font-medium mb-1">Detailed Analytics</h4>
            <p className="text-xs text-muted-foreground">
              Track impressions, clicks, and conversions in real-time
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
